/**
 * Unified Intermediate Data Model for DJ Library Migration
 *
 * This module defines the canonical data structures used as an intermediate
 * representation when migrating between DJ software platforms (Rekordbox,
 * Serato, Traktor, VirtualDJ).
 *
 * Flow: Source Platform -> Parser -> Intermediate Model -> Writer -> Target Platform
 *
 * All parsers convert platform-specific formats INTO these structures.
 * All writers convert FROM these structures into platform-specific formats.
 */

// ============================================================================
// Supported Platforms
// ============================================================================

/**
 * Supported DJ software platforms.
 * @enum {string}
 */
const Platform = {
    REKORDBOX: 'rekordbox',
    SERATO: 'serato',
    TRAKTOR: 'traktor',
    VIRTUALDJ: 'virtualdj',
};

// ============================================================================
// Cue Point Types
// ============================================================================

/**
 * Types of cue points / markers.
 * @enum {string}
 */
const CueType = {
    CUE: 'cue',           // Standard hot cue
    LOOP: 'loop',         // Loop region (has start + end)
    GRID: 'grid',         // Beat grid marker
    FADE_IN: 'fade_in',   // Fade-in point
    FADE_OUT: 'fade_out', // Fade-out point
    LOAD: 'load',         // Load point (auto-cue)
};

// ============================================================================
// Musical Key Notation
// ============================================================================

/**
 * Standard musical key values used in the intermediate model.
 * We use Open Key Notation internally and convert to/from platform-specific
 * key formats in parsers/writers.
 *
 * Open Key notation: 1d-12d (minor), 1m-12m (major)
 * Camelot notation:  1A-12A (minor), 1B-12B (major)
 * Standard:          Am, Cm, etc.
 *
 * Mapping table for reference:
 *   Open Key | Camelot | Standard
 *   1d       | 1A      | Abm
 *   2d       | 2A      | Ebm
 *   3d       | 3A      | Bbm
 *   4d       | 4A      | Fm
 *   5d       | 5A      | Cm
 *   6d       | 6A      | Gm
 *   7d       | 7A      | Dm
 *   8d       | 8A      | Am
 *   9d       | 9A      | Em
 *   10d      | 10A     | Bm
 *   11d      | 11A     | F#m
 *   12d      | 12A     | Dbm
 *   1m       | 1B      | B
 *   2m       | 2B      | F#
 *   3m       | 3B      | Db
 *   4m       | 4B      | Ab
 *   5m       | 5B      | Eb
 *   6m       | 6B      | Bb
 *   7m       | 7B      | F
 *   8m       | 8B      | C
 *   9m       | 9B      | G
 *   10m      | 10B     | D
 *   11m      | 11B     | A
 *   12m      | 12B     | E
 */

// Standard key -> Open Key mapping
const STANDARD_TO_OPEN_KEY = {
    'Abm': '1d', 'G#m': '1d',
    'Ebm': '2d', 'D#m': '2d',
    'Bbm': '3d', 'A#m': '3d',
    'Fm': '4d',
    'Cm': '5d',
    'Gm': '6d',
    'Dm': '7d',
    'Am': '8d',
    'Em': '9d',
    'Bm': '10d',
    'F#m': '11d', 'Gbm': '11d',
    'Dbm': '12d', 'C#m': '12d',
    'B': '1m', 'Cb': '1m',
    'F#': '2m', 'Gb': '2m',
    'Db': '3m', 'C#': '3m',
    'Ab': '4m', 'G#': '4m',
    'Eb': '5m', 'D#': '5m',
    'Bb': '6m', 'A#': '6m',
    'F': '7m',
    'C': '8m',
    'G': '9m',
    'D': '10m',
    'A': '11m',
    'E': '12m',
};

// Open Key -> Standard key mapping
const OPEN_KEY_TO_STANDARD = {
    '1d': 'Abm', '2d': 'Ebm', '3d': 'Bbm', '4d': 'Fm',
    '5d': 'Cm', '6d': 'Gm', '7d': 'Dm', '8d': 'Am',
    '9d': 'Em', '10d': 'Bm', '11d': 'F#m', '12d': 'Dbm',
    '1m': 'B', '2m': 'F#', '3m': 'Db', '4m': 'Ab',
    '5m': 'Eb', '6m': 'Bb', '7m': 'F', '8m': 'C',
    '9m': 'G', '10m': 'D', '11m': 'A', '12m': 'E',
};

// Camelot -> Open Key mapping
const CAMELOT_TO_OPEN_KEY = {
    '1A': '1d', '2A': '2d', '3A': '3d', '4A': '4d',
    '5A': '5d', '6A': '6d', '7A': '7d', '8A': '8d',
    '9A': '9d', '10A': '10d', '11A': '11d', '12A': '12d',
    '1B': '1m', '2B': '2m', '3B': '3m', '4B': '4m',
    '5B': '5m', '6B': '6m', '7B': '7m', '8B': '8m',
    '9B': '9m', '10B': '10m', '11B': '11m', '12B': '12m',
};

// Open Key -> Camelot mapping
const OPEN_KEY_TO_CAMELOT = {
    '1d': '1A', '2d': '2A', '3d': '3A', '4d': '4A',
    '5d': '5A', '6d': '6A', '7d': '7A', '8d': '8A',
    '9d': '9A', '10d': '10A', '11d': '11A', '12d': '12A',
    '1m': '1B', '2m': '2B', '3m': '3B', '4m': '4B',
    '5m': '5B', '6m': '6B', '7m': '7B', '8m': '8B',
    '9m': '9B', '10m': '10B', '11m': '11B', '12m': '12B',
};

// Traktor uses numeric key values (0-23)
const TRAKTOR_KEY_TO_OPEN_KEY = {
    0: '1d', 1: '8m', 2: '2d', 3: '9m', 4: '3d', 5: '10m',
    6: '4d', 7: '11m', 8: '5d', 9: '12m', 10: '6d', 11: '1m',
    12: '7d', 13: '2m', 14: '8d', 15: '3m', 16: '9d', 17: '4m',
    18: '10d', 19: '5m', 20: '11d', 21: '6m', 22: '12d', 23: '7m',
};

const OPEN_KEY_TO_TRAKTOR_KEY = {};
for (const [traktorKey, openKey] of Object.entries(TRAKTOR_KEY_TO_OPEN_KEY)) {
    OPEN_KEY_TO_TRAKTOR_KEY[openKey] = parseInt(traktorKey);
}

/**
 * Convert any key notation to Open Key format.
 * Accepts standard notation (Am, C#m, F), Camelot (8A, 1B), Open Key (8d, 1m),
 * or Traktor numeric keys.
 *
 * @param {string|number|null} key - The key in any supported notation
 * @returns {string|null} The key in Open Key notation, or null if unrecognized
 */
function normalizeKey(key) {
    if (key === null || key === undefined || key === '') return null;

    const keyStr = String(key).trim();
    if (!keyStr) return null;

    // Already Open Key format?
    if (/^\d{1,2}[dm]$/.test(keyStr)) {
        return OPEN_KEY_TO_STANDARD[keyStr] ? keyStr : null;
    }

    // Camelot format?
    if (/^\d{1,2}[AB]$/.test(keyStr)) {
        return CAMELOT_TO_OPEN_KEY[keyStr] || null;
    }

    // Traktor numeric key?
    const numKey = parseInt(keyStr);
    if (!isNaN(numKey) && numKey >= 0 && numKey <= 23) {
        return TRAKTOR_KEY_TO_OPEN_KEY[numKey] || null;
    }

    // Standard notation (Am, C#m, Bb, F#, etc.)
    if (STANDARD_TO_OPEN_KEY[keyStr]) return STANDARD_TO_OPEN_KEY[keyStr];

    // Normalize verbose variants: "Cmin" -> "Cm", "Cmaj" -> "C", "C minor" -> "Cm", "C major" -> "C"
    const normalized = keyStr
        .replace(/\s*(minor|min)\s*$/i, 'm')
        .replace(/\s*(major|maj)\s*$/i, '')
        .trim();
    return STANDARD_TO_OPEN_KEY[normalized] || null;
}

/**
 * Convert Open Key to standard musical notation.
 * @param {string|null} openKey
 * @returns {string|null}
 */
function openKeyToStandard(openKey) {
    if (!openKey) return null;
    return OPEN_KEY_TO_STANDARD[openKey] || null;
}

/**
 * Convert Open Key to Camelot notation.
 * @param {string|null} openKey
 * @returns {string|null}
 */
function openKeyToCamelot(openKey) {
    if (!openKey) return null;
    return OPEN_KEY_TO_CAMELOT[openKey] || null;
}

/**
 * Convert Open Key to Traktor numeric key.
 * @param {string|null} openKey
 * @returns {number|null}
 */
function openKeyToTraktorKey(openKey) {
    if (!openKey) return null;
    const val = OPEN_KEY_TO_TRAKTOR_KEY[openKey];
    return val !== undefined ? val : null;
}

// ============================================================================
// Color Utilities
// ============================================================================

/**
 * Predefined cue point colors.
 * Each platform has its own color palette. This table provides a common set
 * of colors and their hex values for cross-platform mapping.
 */
const CueColor = {
    RED: '#FF0000',
    ORANGE: '#FF8C00',
    YELLOW: '#FFFF00',
    GREEN: '#00FF00',
    AQUA: '#00FFFF',
    BLUE: '#0000FF',
    PURPLE: '#8B00FF',
    PINK: '#FF69B4',
};

// Rekordbox color codes (used in XML @_Colour attributes) -> hex
const REKORDBOX_COLORS = {
    '-1': null,            // No color
    '-16777216': '#FF0000', // Red (displayed in RB as red)
    '-65536': '#FF0000',
    '-256': '#FFFF00',
    '-16711936': '#00FF00',
    '-16776961': '#0000FF',
    '-32768': '#FF8C00',
};

// ============================================================================
// Data Structures (factory functions)
// ============================================================================

/**
 * Create a CuePoint object.
 *
 * @param {Object} opts
 * @param {string} [opts.name='']         - Display name for this cue
 * @param {string} opts.type              - One of CueType values
 * @param {number} opts.positionMs        - Position in milliseconds from start of track
 * @param {number|null} [opts.endPositionMs=null] - End position for loops (ms)
 * @param {number} [opts.hotcueIndex=-1]  - Hot cue slot index (0-7 typically), -1 if not a hot cue
 * @param {string|null} [opts.color=null] - Hex color string (e.g. '#FF0000')
 * @returns {Object} CuePoint
 */
function createCuePoint(opts = {}) {
    return {
        name: opts.name || '',
        type: opts.type || CueType.CUE,
        positionMs: opts.positionMs || 0,
        endPositionMs: opts.endPositionMs || null,
        hotcueIndex: opts.hotcueIndex !== undefined ? opts.hotcueIndex : -1,
        color: opts.color || null,
    };
}

/**
 * Map cue points onto a SINGLE shared bank of 8 hot-cue pads.
 *
 * Serato keeps cues and loops in independent 0-7 pad banks, and the intermediate
 * model follows that (a cue and a loop may both carry hotcueIndex 0). Rekordbox
 * and Traktor have ONE bank of 8 pads shared by cues and loops — writing the
 * model's indices verbatim there creates two entries on the same pad, and the
 * app hides/garbles them (pads showed different points than MixGoat).
 *
 * Placement policy (user setting, Cue Points > Loops > "Save loops as"):
 *   'spare'    - cues keep their pads first, loops fill the remaining free
 *                pads in position order (default; nothing is lost — extras
 *                become memory cues/loops).
 *   'memory'   - loops NEVER take pads; every loop becomes a memory loop.
 *   'priority' - loops claim pads first (own pad or next free, in position
 *                order), then cues take their pad if still free, else the
 *                next free pad.
 *
 * @param {Object[]} cuePoints - CuePoint objects (model semantics)
 * @param {string} [policy='spare'] - 'spare' | 'memory' | 'priority'
 * @returns {(number|null)[]} pad slot (0-7) per cue point, null = no pad
 */
/**
 * Rekordbox DjmdCue.Kind → hot-cue pad slot (0-7 = A-H), or -1 if not a pad.
 *
 * Rekordbox does NOT use Kind 1-8 for pads A-H (pyrekordbox's docs are wrong
 * for current versions). Verified against rows rekordbox 7 wrote itself
 * (2026-07, all 8 pads set in the app): pads A-H = Kind 1,2,3,5,6,7,8,9.
 * Kind 0 = memory cue/loop; Kind 4 = reserved legacy value (old 'loop' type)
 * that rekordbox demotes to memory if a pad row carries it.
 *
 * @param {number} kind - DjmdCue.Kind value
 * @returns {number} pad slot 0-7, or -1 (memory / not a pad)
 */
function rekordboxKindToPadSlot(kind) {
    if (!Number.isInteger(kind)) return -1;
    if (kind >= 1 && kind <= 3) return kind - 1;
    if (kind >= 5 && kind <= 9) return kind - 2;
    return -1;
}

function assignSharedHotcueSlots(cuePoints, policy = 'spare') {
    const points = cuePoints || [];
    const used = new Set();
    const slots = points.map(() => null);

    const takeOwnOrNextFree = ({ c, i }) => {
        if (Number.isInteger(c.hotcueIndex) && c.hotcueIndex >= 0 && c.hotcueIndex <= 7 && !used.has(c.hotcueIndex)) {
            slots[i] = c.hotcueIndex;
            used.add(c.hotcueIndex);
            return;
        }
        for (let s = 0; s < 8; s++) {
            if (!used.has(s)) { slots[i] = s; used.add(s); return; }
        }
    };

    const slottedLoops = points
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => c.type === CueType.LOOP && Number.isInteger(c.hotcueIndex) && c.hotcueIndex >= 0)
        .sort((a, b) => (a.c.positionMs || 0) - (b.c.positionMs || 0));
    const slottedCues = points
        .map((c, i) => ({ c, i }))
        .filter(({ c }) => c.type !== CueType.LOOP && Number.isInteger(c.hotcueIndex) && c.hotcueIndex >= 0)
        .sort((a, b) => (a.c.positionMs || 0) - (b.c.positionMs || 0));

    if (policy === 'priority') {
        // Loops claim pads first, then cues fill what's left.
        slottedLoops.forEach(takeOwnOrNextFree);
        slottedCues.forEach(takeOwnOrNextFree);
        return slots;
    }

    // 'spare' (default) and 'memory': cues with an explicit unique pad keep it.
    slottedCues.forEach(({ c, i }) => {
        if (c.hotcueIndex <= 7 && !used.has(c.hotcueIndex)) {
            slots[i] = c.hotcueIndex;
            used.add(c.hotcueIndex);
        }
    });

    // 'memory': loops never take pads — they all become memory loops.
    if (policy !== 'memory') {
        slottedLoops.forEach(takeOwnOrNextFree);
    }

    return slots;
}

/**
 * Create a BeatGridMarker object.
 * Beat grids define the tempo map of a track - a series of markers that
 * indicate where beats fall. Each marker has a position and a BPM that
 * applies from that position until the next marker.
 *
 * @param {Object} opts
 * @param {number} opts.positionMs   - Position of the beat marker in milliseconds
 * @param {number} opts.bpm          - BPM starting from this marker position
 * @param {number} [opts.beatsPerBar=4] - Beats per bar (time signature numerator)
 * @param {number} [opts.beatNumber=1]  - Which beat in the bar this marker is on (1-based)
 * @returns {Object} BeatGridMarker
 */
function createBeatGridMarker(opts = {}) {
    return {
        positionMs: opts.positionMs || 0,
        bpm: opts.bpm || 0,
        beatsPerBar: opts.beatsPerBar || 4,
        beatNumber: opts.beatNumber || 1,
    };
}

/**
 * Create a Track object - the core unit of the migration model.
 *
 * @param {Object} opts
 * @param {string} opts.filePath          - Absolute file path on disk
 * @param {string} [opts.title='']        - Track title
 * @param {string} [opts.artist='']       - Artist name
 * @param {string} [opts.album='']        - Album name
 * @param {string} [opts.genre='']        - Genre
 * @param {string} [opts.label='']        - Record label
 * @param {string} [opts.comment='']      - User comment
 * @param {string|null} [opts.year=null]  - Release year (string, e.g. '2023')
 * @param {number|null} [opts.bpm=null]   - Beats per minute
 * @param {string|null} [opts.key=null]   - Musical key in Open Key notation (normalized)
 * @param {number|null} [opts.rating=null] - Rating on a 0-5 scale (0 = unrated)
 * @param {number|null} [opts.duration=null] - Duration in seconds
 * @param {number} [opts.playCount=0]     - Number of times played
 * @param {string|null} [opts.color=null] - Track color (hex)
 * @param {string|null} [opts.grouping=''] - Grouping field
 * @param {string|null} [opts.remixer='']  - Remixer name
 * @param {number|null} [opts.bitrate=null] - Audio bitrate in kbps
 * @param {number|null} [opts.sampleRate=null] - Sample rate in Hz
 * @param {string|null} [opts.fileType=null]   - File type (e.g. 'mp3', 'flac', 'wav')
 * @param {Object[]} [opts.cuePoints=[]]    - Array of CuePoint objects
 * @param {Object[]} [opts.beatGrid=[]]     - Array of BeatGridMarker objects
 * @param {Object} [opts.platformMeta={}]   - Platform-specific metadata to preserve
 * @returns {Object} Track
 */
function createTrack(opts = {}) {
    return {
        filePath: opts.filePath || '',
        title: opts.title || '',
        artist: opts.artist || '',
        album: opts.album || '',
        genre: opts.genre || '',
        label: opts.label || '',
        comment: opts.comment || '',
        year: opts.year || null,
        bpm: opts.bpm || null,
        key: opts.key || null,
        rating: opts.rating !== undefined ? opts.rating : null,
        duration: opts.duration || null,
        playCount: opts.playCount || 0,
        color: opts.color || null,
        grouping: opts.grouping || '',
        remixer: opts.remixer || '',
        bitrate: opts.bitrate || null,
        sampleRate: opts.sampleRate || null,
        fileType: opts.fileType || null,
        cuePoints: opts.cuePoints || [],
        beatGrid: opts.beatGrid || [],
        platformMeta: opts.platformMeta || {},
    };
}

/**
 * Create a Playlist object.
 *
 * @param {Object} opts
 * @param {string} opts.name              - Playlist name
 * @param {string} [opts.path='']         - Full path in folder hierarchy (e.g. 'Folder/SubFolder/PlaylistName')
 * @param {Object[]} [opts.tracks=[]]     - Array of Track objects in playlist order
 * @param {boolean} [opts.isFolder=false] - True if this is a folder node (contains sub-playlists, not tracks)
 * @param {Object[]} [opts.children=[]]   - Child playlists/folders (if isFolder=true)
 * @returns {Object} Playlist
 */
function createPlaylist(opts = {}) {
    return {
        name: opts.name || '',
        path: opts.path || '',
        // Target app's own playlist id when known (linked playlist) — writers
        // that support it update in place instead of creating a twin.
        sourceId: opts.sourceId || null,
        tracks: opts.tracks || [],
        isFolder: opts.isFolder || false,
        children: opts.children || [],
    };
}

/**
 * Create a MigrationLibrary - the top-level container for all migration data.
 * This is what parsers return and writers consume.
 *
 * @param {Object} opts
 * @param {string} opts.sourcePlatform    - Source platform (one of Platform values)
 * @param {string} [opts.sourcePath='']   - Path to the source library file/directory
 * @param {Object[]} [opts.tracks=[]]     - Array of Track objects (full collection)
 * @param {Object[]} [opts.playlists=[]]  - Array of Playlist objects
 * @param {Object} [opts.metadata={}]     - Additional source metadata (version info, etc.)
 * @returns {Object} MigrationLibrary
 */
function createMigrationLibrary(opts = {}) {
    return {
        sourcePlatform: opts.sourcePlatform || '',
        sourcePath: opts.sourcePath || '',
        tracks: opts.tracks || [],
        playlists: opts.playlists || [],
        metadata: opts.metadata || {},
    };
}

/**
 * Create a MigrationResult - returned after a migration completes.
 *
 * @param {Object} opts
 * @param {boolean} [opts.success=false]
 * @param {string} opts.sourcePlatform
 * @param {string} opts.targetPlatform
 * @param {string} [opts.outputPath='']
 * @param {number} [opts.tracksConverted=0]
 * @param {number} [opts.playlistsConverted=0]
 * @param {number} [opts.cuePointsConverted=0]
 * @param {number} [opts.warnings=0]
 * @param {string[]} [opts.warningMessages=[]]
 * @param {string[]} [opts.errors=[]]
 * @returns {Object} MigrationResult
 */
function createMigrationResult(opts = {}) {
    return {
        success: opts.success || false,
        sourcePlatform: opts.sourcePlatform || '',
        targetPlatform: opts.targetPlatform || '',
        outputPath: opts.outputPath || '',
        tracksConverted: opts.tracksConverted || 0,
        playlistsConverted: opts.playlistsConverted || 0,
        cuePointsConverted: opts.cuePointsConverted || 0,
        warnings: opts.warnings || 0,
        warningMessages: opts.warningMessages || [],
        errors: opts.errors || [],
        method: opts.method || null,
    };
}

// ============================================================================
// Rating Conversion Utilities
// ============================================================================

/**
 * Normalize a rating to 0-5 scale from various platform scales.
 *
 * Rekordbox: 0, 51, 102, 153, 204, 255 -> 0-5
 * Serato:    0-5 (already normalized)
 * Traktor:   0-255 -> 0-5
 * VirtualDJ: 0-5 (stars, already normalized)
 *
 * @param {number|null} rating - Raw rating value
 * @param {string} platform - Source platform
 * @returns {number|null} Rating on 0-5 scale
 */
function normalizeRating(rating, platform) {
    if (rating === null || rating === undefined) return null;
    const r = Number(rating);
    if (isNaN(r)) return null;

    switch (platform) {
        case Platform.REKORDBOX:
            // Rekordbox uses 0, 51, 102, 153, 204, 255
            return Math.round(r / 51);
        case Platform.TRAKTOR:
            // Traktor uses 0-255
            return Math.round((r / 255) * 5);
        case Platform.SERATO:
        case Platform.VIRTUALDJ:
            // Already 0-5
            return Math.min(5, Math.max(0, Math.round(r)));
        default:
            return Math.min(5, Math.max(0, Math.round(r)));
    }
}

/**
 * Convert normalized 0-5 rating to platform-specific value.
 *
 * @param {number|null} rating - Rating on 0-5 scale
 * @param {string} platform - Target platform
 * @returns {number|null} Platform-specific rating
 */
function denormalizeRating(rating, platform) {
    if (rating === null || rating === undefined) return null;
    const r = Math.min(5, Math.max(0, Math.round(Number(rating))));
    if (isNaN(r)) return null;

    switch (platform) {
        case Platform.REKORDBOX:
            return r * 51;
        case Platform.TRAKTOR:
            return Math.round((r / 5) * 255);
        case Platform.SERATO:
        case Platform.VIRTUALDJ:
            return r;
        default:
            return r;
    }
}

// ============================================================================
// Exports
// ============================================================================

module.exports = {
    // Enums
    Platform,
    CueType,
    CueColor,

    // Slot mapping
    assignSharedHotcueSlots,
    rekordboxKindToPadSlot,

    // Factory functions
    createCuePoint,
    createBeatGridMarker,
    createTrack,
    createPlaylist,
    createMigrationLibrary,
    createMigrationResult,

    // Key utilities
    normalizeKey,
    openKeyToStandard,
    openKeyToCamelot,
    openKeyToTraktorKey,
    STANDARD_TO_OPEN_KEY,
    OPEN_KEY_TO_STANDARD,
    CAMELOT_TO_OPEN_KEY,
    OPEN_KEY_TO_CAMELOT,
    TRAKTOR_KEY_TO_OPEN_KEY,
    OPEN_KEY_TO_TRAKTOR_KEY,

    // Rating utilities
    normalizeRating,
    denormalizeRating,

    // Color constants
    REKORDBOX_COLORS,
};
